/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useMemo } from 'react';
import { UNLICENSED } from '@jira/platform__application-editions/src/edition.tsx';
import { isCMDBEnabledInEnvironment } from '@jira/servicedesk__insight-common-utils-cmdb-fedramp/src/isCMDBEnabledInEnvironment.tsx';
import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
import { isFedrampModerate } from '@atlaskit/atlassian-context/is-fedramp-moderate';
import { fg } from '@atlassian/jira-feature-gating';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import {
	CONFIGURABLE_MENU_IDS_AS_STRINGS,
	GLOBAL_APP_MENU_IDS_AS_STRINGS,
} from '../../constants/index.tsx';
import { L1_MENU_ID, type GlobalAppMenuId } from '../../types.tsx';
import { useAssetsSidebarState } from '@jira/navigation-apps__sidebar-nav4-assets/src/useAssetsSidebarState.tsx';

export type GlobalAppsLicense = {
	goals: boolean;
	assets: boolean;
	confluence: boolean;
	loom: boolean;
	projects: boolean;
	teams: boolean;
	opsgenie: boolean;
};

/**
 * Assets should show when there's a JSM license
 * Goals should show for all other Jira products but not when it's JSM-only
 */
export function useGlobalApps(): GlobalAppsLicense {
	const appEditions = useAppEditions();

	const shouldShowGoals =
		(appEditions.productDiscovery !== UNLICENSED ||
			appEditions.software !== UNLICENSED ||
			// JSM comes with a free core license. Goals don't display with JSM-only, so checking for unlicensed JSM
			(appEditions.core !== UNLICENSED && appEditions.serviceDesk === UNLICENSED)) &&
		(fg('aem_nav_common_core') ? isFeatureEnabled('jira', 'jira-goals-nav') : !isFedrampModerate());

	const { isVisible: isAssetsVisible } = useAssetsSidebarState();
	const shouldShowAssets =
		isAssetsVisible && fg('assets_as_an_app_v2') && isCMDBEnabledInEnvironment();
	const globalAppShortcuts = useMemo(() => {
		if (appEditions.core !== UNLICENSED) {
			if (appEditions.serviceDesk !== UNLICENSED) {
				return {
					confluence: true,
					loom: true,
					projects: false,
					teams: true,
					opsgenie: true,
				};
			}

			if (appEditions.productDiscovery !== UNLICENSED) {
				return {
					confluence: true,
					loom: true,
					projects: false,
					teams: true,
					opsgenie: false,
				};
			}
		} else {
			if (appEditions.serviceDesk !== UNLICENSED) {
				return {
					confluence: false,
					loom: false,
					projects: false,
					teams: true,
					opsgenie: true,
				};
			}

			if (appEditions.productDiscovery !== UNLICENSED) {
				return {
					confluence: false,
					loom: false,
					projects: false,
					teams: true,
					opsgenie: false,
				};
			}
		}

		return {
			confluence: true,
			loom: true,
			projects: true,
			teams: true,
			opsgenie: false,
		};
	}, [appEditions]);

	return useMemo(() => {
		return {
			goals: shouldShowGoals,
			assets: shouldShowAssets,
			...globalAppShortcuts,
		};
	}, [globalAppShortcuts, shouldShowAssets, shouldShowGoals]);
}

export const isSupportedMenuId = (value: string): value is GlobalAppMenuId =>
	CONFIGURABLE_MENU_IDS_AS_STRINGS.includes(value);

export const isGlobalAppMenuId = (value: string): value is GlobalAppMenuId =>
	GLOBAL_APP_MENU_IDS_AS_STRINGS.includes(value);

export const hasGlobalAppLicense = (
	value: string,
	globalAppLicense: GlobalAppsLicense,
): value is GlobalAppMenuId => {
	switch (value) {
		case L1_MENU_ID.GLOBAL_APP_ASSETS:
			return globalAppLicense.assets;
		case L1_MENU_ID.GLOBAL_APP_GOALS:
			return globalAppLicense.goals;
		case L1_MENU_ID.GLOBAL_APP_CONFLUENCE:
			return globalAppLicense.confluence;
		case L1_MENU_ID.GLOBAL_APP_LOOM:
			return globalAppLicense.loom;
		case L1_MENU_ID.GLOBAL_APP_PROJECTS:
			return globalAppLicense.projects;
		case L1_MENU_ID.GLOBAL_APP_TEAMS:
			return globalAppLicense.teams;
		default:
			return false;
	}
};
